import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppConfig } from '@mkp/shared/util-app-config';
import { CLIENT_ENVIRONMENT_PROVIDER } from '@mkp/shared/util-environment/client';
import { inviteApiActions } from '@mkp/account-membership/actions';
import { authApiActions } from '@mkp/auth/actions';
import { AuthFacade } from '@mkp/auth/util';
import { appInitFactoryActions } from '@mkp/core/state/actions';
import { LanguageMapper } from '@mkp/shared/models';
import { SnackbarService } from '@mkp/shared/util-snackbar';
import { getAuthRedirectUri, WEBAUTH_SERVICE_TOKEN } from '@mkp/shared/util-auth';
import { generatePasswordlessLoginRedirectUri } from '@mkp/user/util-passwordless-login';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { WebAuth } from 'auth0-js';
import { Observable, of } from 'rxjs';
import { catchError, delay, exhaustMap, map, retry, switchMap, tap } from 'rxjs/operators';

const { routes } = AppConfig;
const AUTH0DELAY = 500;

@Injectable()
export class AuthApiEffects {
  private readonly actions$ = inject(Actions);
  private readonly authFacade = inject(AuthFacade);
  private readonly webAuth = inject(WEBAUTH_SERVICE_TOKEN);
  private readonly translateService = inject(TranslateService);
  private readonly router = inject(Router);
  private readonly snackbar = inject(SnackbarService);
  private readonly environment = inject(CLIENT_ENVIRONMENT_PROVIDER);

  readonly fetchAccessToken$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(appInitFactoryActions.enter),
        switchMap(() =>
          this.authFacade.fetchAccessToken().pipe(
            map(({ actorId, zendeskJWT }) =>
              authApiActions.fetchAccessTokenSuccess({ actorId, zendeskJWT })
            ),
            catchError((error: unknown) => of(authApiActions.fetchAccessTokenFailure({ error })))
          )
        )
      );
    },
    { useEffectsErrorHandler: false }
  );

  readonly sendPasswordlessLoginLink$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(inviteApiActions.redeemInviteAfterUserCreationSuccess),
        delay(AUTH0DELAY), //TODO (no ticket atm, check with CDA), sometimes there is a race condition between auth0 and user creation, auth0 is "too fast" and misses the user creation in db
        exhaustMap(({ email }) =>
          handleWebAuthLinkSending(
            this.translateService,
            this.webAuth,
            email,
            getAuthRedirectUri(this.environment.auth)
          ).pipe(
            retry({ count: 3, delay: AUTH0DELAY }),
            map(() => authApiActions.sendPasswordlessLinkSuccess({ email })),
            catchError((error: unknown) =>
              of(authApiActions.sendPasswordlessLinkFailure({ error }))
            )
          )
        )
      ),
    { useEffectsErrorHandler: false }
  );

  readonly sendPasswordlessLinkSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authApiActions.sendPasswordlessLinkSuccess),
        tap(() =>
          this.router.navigate([routes.passwordlessLogin, routes.passwordlessLoginLinkSent])
        )
      ),
    { dispatch: false }
  );

  readonly sendPasswordlessLinkFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authApiActions.sendPasswordlessLinkFailure),
        tap(() => {
          this.router.navigate([routes.home]);
          this.snackbar.showError('REQUEST_TIMEOUT_MESSAGE');
        })
      ),
    { dispatch: false }
  );
}

const handleWebAuthLinkSending = (
  translateService: TranslateService,
  webAuth: WebAuth,
  email: string,
  redirectUri: string
) =>
  new Observable((observer) => {
    const redirectUriWithUtm = generatePasswordlessLoginRedirectUri(
      redirectUri,
      LanguageMapper.toCode(translateService.getCurrentLang() || translateService.getFallbackLang())
    );

    webAuth.passwordlessStart(
      {
        connection: 'email',
        send: 'link',
        email,
        authParams: { redirectUri: redirectUriWithUtm },
      },
      (err, result) => (err ? observer.error(err) : (observer.next(result), observer.complete()))
    );
  });
